import { openDB, deleteDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { DeviceRegistration, DeviceManifest, PlayEvent } from '../types';
import { PLAYER_CONFIG } from '../config';
import { STORAGE_KEYS } from '../types';

// Migrate localStorage keys from old goled_* prefix to govista_*
function migrateLocalStorageKeys() {
  const migrations: [string, string][] = [
    ['goled_device_registration', 'govista_device_registration'],
    ['goled_manifest', 'govista_manifest'],
    ['goled_cached_assets', 'govista_cached_assets'],
    ['goled_pending_analytics', 'govista_pending_analytics'],
    ['goled_player_state', 'govista_player_state'],
    ['goled_device_uuid', 'govista_device_uuid'],
    ['goled_hardware_id', 'govista_hardware_id'],
    ['goled_clean_shutdown', 'govista_clean_shutdown'],
    ['goled_last_good_manifest', 'govista_last_good_manifest'],
    ['goled_fallback_assets', 'govista_fallback_assets'],
    ['goled_network_status', 'govista_network_status'],
  ];

  for (const [oldKey, newKey] of migrations) {
    const value = localStorage.getItem(oldKey);
    if (value !== null) {
      localStorage.setItem(newKey, value);
      localStorage.removeItem(oldKey);
    }
  }
}

// Migrate IndexedDB from old 'goled-player' to 'govista-player'
async function migrateIndexedDB(): Promise<void> {
  try {
    // Check if old database exists
    const databases = await indexedDB.databases();
    const oldDbExists = databases.some(db => db.name === 'goled-player');
    if (!oldDbExists) return;

    const newDbExists = databases.some(db => db.name === 'govista-player');
    if (newDbExists) {
      // New DB already exists, just clean up old
      await deleteDB('goled-player');
      return;
    }

    // Open old database
    const oldDb = await openDB('goled-player', 1);
    const newDb = await openDB<GoVistaDB>('govista-player', 1, {
      upgrade(database) {
        if (!database.objectStoreNames.contains('config')) {
          database.createObjectStore('config');
        }
        if (!database.objectStoreNames.contains('assets')) {
          const assetsStore = database.createObjectStore('assets', { keyPath: 'id' });
          assetsStore.createIndex('by-cached-at', 'cachedAt');
        }
        if (!database.objectStoreNames.contains('analytics')) {
          const analyticsStore = database.createObjectStore('analytics', { keyPath: 'id' });
          analyticsStore.createIndex('by-synced', 'synced');
        }
      },
    });

    // Copy data from each store
    for (const storeName of ['config', 'assets', 'analytics'] as const) {
      if (oldDb.objectStoreNames.contains(storeName)) {
        const tx = oldDb.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const allKeys = await store.getAllKeys();
        const allValues = await store.getAll();

        const newTx = newDb.transaction(storeName, 'readwrite');
        const newStore = newTx.objectStore(storeName);
        for (let i = 0; i < allKeys.length; i++) {
          if (storeName === 'config') {
            await newStore.put(allValues[i], allKeys[i] as string);
          } else {
            await newStore.put(allValues[i]);
          }
        }
        await newTx.done;
      }
    }

    oldDb.close();
    newDb.close();

    // Delete old database
    await deleteDB('goled-player');
    console.log('[Storage] Successfully migrated IndexedDB from goled-player to govista-player');
  } catch (error) {
    console.warn('[Storage] IndexedDB migration failed (non-fatal):', error);
  }
}

// Run migrations on module load
try {
  migrateLocalStorageKeys();
} catch {
  // Ignore migration errors in non-browser environments
}

// IndexedDB schema
interface GoVistaDB extends DBSchema {
  config: {
    key: string;
    value: unknown;
  };
  assets: {
    key: string;
    value: {
      id: string;
      url: string;
      blob: Blob;
      mimeType: string;
      cachedAt: number;
      lastAccessed: number;
      size: number;
    };
    indexes: { 'by-cached-at': number };
  };
  analytics: {
    key: string;
    value: {
      id: string;
      type: 'play' | 'session' | 'heartbeat';
      data: PlayEvent | Record<string, unknown>;
      createdAt: number;
      synced: number; // 0 = not synced, 1 = synced (boolean not valid for IndexedDB keys)
    };
    indexes: { 'by-synced': number };
  };
}

let db: IDBPDatabase<GoVistaDB> | null = null;
let dbPromise: Promise<IDBPDatabase<GoVistaDB>> | null = null;

// Check if database connection is valid
function isDBValid(database: IDBPDatabase<GoVistaDB> | null): database is IDBPDatabase<GoVistaDB> {
  if (!database) return false;
  try {
    // Check if the database connection is still open
    return database.objectStoreNames.length > 0;
  } catch {
    return false;
  }
}

// Initialize database with retry logic
export async function initDB(): Promise<IDBPDatabase<GoVistaDB>> {
  // Return existing valid connection
  if (isDBValid(db)) return db;

  // Return pending promise to avoid race conditions
  if (dbPromise) {
    try {
      const result = await dbPromise;
      if (isDBValid(result)) return result;
    } catch {
      // Connection failed, will retry below
    }
  }

  // Reset state
  db = null;

  // Migrate old IndexedDB before opening new one
  await migrateIndexedDB();

  dbPromise = openDB<GoVistaDB>('govista-player', 1, {
    upgrade(database) {
      // Config store
      if (!database.objectStoreNames.contains('config')) {
        database.createObjectStore('config');
      }

      // Assets store
      if (!database.objectStoreNames.contains('assets')) {
        const assetsStore = database.createObjectStore('assets', { keyPath: 'id' });
        assetsStore.createIndex('by-cached-at', 'cachedAt');
      }

      // Analytics store
      if (!database.objectStoreNames.contains('analytics')) {
        const analyticsStore = database.createObjectStore('analytics', { keyPath: 'id' });
        analyticsStore.createIndex('by-synced', 'synced');
      }
    },
    blocked() {
      console.warn('[Storage] Database blocked by another connection');
    },
    blocking() {
      console.warn('[Storage] This connection is blocking a newer version');
      // Close this connection to allow upgrade
      if (db) {
        db.close();
        db = null;
      }
    },
    terminated() {
      console.warn('[Storage] Database connection terminated unexpectedly');
      db = null;
      dbPromise = null;
    },
  });

  db = await dbPromise;
  return db;
}

// Wrapper for database operations with retry logic
async function withDB<T>(
  operation: (database: IDBPDatabase<GoVistaDB>) => Promise<T>,
  retries = 2
): Promise<T> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const database = await initDB();
      return await operation(database);
    } catch (error) {
      const isClosingError = error instanceof Error &&
        (error.message.includes('closing') || error.message.includes('InvalidStateError'));

      if (isClosingError && attempt < retries) {
        console.warn(`[Storage] Database operation failed (attempt ${attempt + 1}), retrying...`);
        // Reset connection and retry
        db = null;
        dbPromise = null;
        await new Promise(resolve => setTimeout(resolve, 100 * (attempt + 1)));
        continue;
      }
      throw error;
    }
  }
  throw new Error('Database operation failed after retries');
}

// Config operations
export async function getConfig<T>(key: string): Promise<T | undefined> {
  return withDB(async (database) => {
    return database.get('config', key) as Promise<T | undefined>;
  });
}

export async function setConfig<T>(key: string, value: T): Promise<void> {
  return withDB(async (database) => {
    await database.put('config', value, key);
  });
}

export async function deleteConfig(key: string): Promise<void> {
  return withDB(async (database) => {
    await database.delete('config', key);
  });
}

// Device registration
export async function getDeviceRegistration(): Promise<DeviceRegistration | undefined> {
  return getConfig<DeviceRegistration>(STORAGE_KEYS.DEVICE_REGISTRATION);
}

export async function setDeviceRegistration(reg: DeviceRegistration): Promise<void> {
  return setConfig(STORAGE_KEYS.DEVICE_REGISTRATION, reg);
}

// Manifest
export async function getManifest(): Promise<DeviceManifest | undefined> {
  return getConfig<DeviceManifest>(STORAGE_KEYS.MANIFEST);
}

export async function setManifest(manifest: DeviceManifest): Promise<void> {
  return setConfig(STORAGE_KEYS.MANIFEST, manifest);
}

// Asset caching
export async function getCachedAsset(id: string): Promise<Blob | undefined> {
  return withDB(async (database) => {
    const asset = await database.get('assets', id);

    if (asset) {
      // Update last accessed time (non-blocking, don't wait)
      database.put('assets', {
        ...asset,
        lastAccessed: Date.now(),
      }).catch(() => {
        // Ignore errors on last accessed update
      });
      return asset.blob;
    }

    return undefined;
  });
}

export async function cacheAsset(
  id: string,
  url: string,
  blob: Blob,
  mimeType: string,
  skipEviction = false
): Promise<void> {
  return withDB(async (database) => {
    // Check cache size and evict if needed
    // Skip eviction during bulk downloads to preserve fallback assets
    if (!skipEviction) {
      await evictOldAssets();
    }

    await database.put('assets', {
      id,
      url,
      blob,
      mimeType,
      cachedAt: Date.now(),
      lastAccessed: Date.now(),
      size: blob.size,
    });
  });
}

export async function deleteCachedAsset(id: string): Promise<void> {
  return withDB(async (database) => {
    await database.delete('assets', id);
  });
}

async function evictOldAssets(): Promise<void> {
  try {
    await withDB(async (database) => {
      const tx = database.transaction('assets', 'readwrite');
      const store = tx.objectStore('assets');
      const index = store.index('by-cached-at');

      const count = await store.count();

      if (count >= PLAYER_CONFIG.MAX_CACHED_ASSETS) {
        // Delete oldest 10% of assets
        const toDelete = Math.ceil(count * 0.1);
        let cursor = await index.openCursor();
        let deleted = 0;

        while (cursor && deleted < toDelete) {
          await cursor.delete();
          deleted++;
          cursor = await cursor.continue();
        }
      }

      // Also delete expired assets
      const expiryTime = Date.now() - PLAYER_CONFIG.CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      let cursor = await index.openCursor(IDBKeyRange.upperBound(expiryTime));

      while (cursor) {
        await cursor.delete();
        cursor = await cursor.continue();
      }

      await tx.done;
    });
  } catch (error) {
    // Don't fail the main operation if eviction fails
    console.warn('[Storage] Failed to evict old assets:', error);
  }
}

// Analytics
export async function addAnalyticsEvent(
  type: 'play' | 'session' | 'heartbeat',
  data: PlayEvent | Record<string, unknown>
): Promise<void> {
  try {
    await withDB(async (database) => {
      await database.add('analytics', {
        id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type,
        data,
        createdAt: Date.now(),
        synced: 0, // 0 = not synced
      });
    });
  } catch (error) {
    // Don't fail if analytics can't be recorded
    console.warn('[Storage] Failed to add analytics event:', error);
  }
}

export async function getPendingAnalytics(): Promise<
  Array<{
    id: string;
    type: 'play' | 'session' | 'heartbeat';
    data: PlayEvent | Record<string, unknown>;
    createdAt: number;
  }>
> {
  return withDB(async (database) => {
    const tx = database.transaction('analytics', 'readonly');
    const index = tx.objectStore('analytics').index('by-synced');

    const pending: Array<{
      id: string;
      type: 'play' | 'session' | 'heartbeat';
      data: PlayEvent | Record<string, unknown>;
      createdAt: number;
    }> = [];

    let cursor = await index.openCursor(IDBKeyRange.only(0)); // 0 = not synced

    while (cursor) {
      pending.push({
        id: cursor.value.id,
        type: cursor.value.type,
        data: cursor.value.data,
        createdAt: cursor.value.createdAt,
      });
      cursor = await cursor.continue();
    }

    return pending;
  });
}

export async function markAnalyticsSynced(ids: string[]): Promise<void> {
  return withDB(async (database) => {
    const tx = database.transaction('analytics', 'readwrite');
    const store = tx.objectStore('analytics');

    for (const id of ids) {
      const item = await store.get(id);
      if (item) {
        await store.put({ ...item, synced: 1 }); // 1 = synced
      }
    }

    await tx.done;
  });
}

export async function clearSyncedAnalytics(): Promise<void> {
  return withDB(async (database) => {
    const tx = database.transaction('analytics', 'readwrite');
    const index = tx.objectStore('analytics').index('by-synced');

    let cursor = await index.openCursor(IDBKeyRange.only(1)); // 1 = synced

    while (cursor) {
      await cursor.delete();
      cursor = await cursor.continue();
    }

    await tx.done;
  });
}

// Get all cached asset IDs
export async function getCachedAssetIds(): Promise<string[]> {
  return withDB(async (database) => {
    return database.getAllKeys('assets') as Promise<string[]>;
  });
}

// Clear all data
export async function clearAllData(): Promise<void> {
  return withDB(async (database) => {
    await database.clear('config');
    await database.clear('assets');
    await database.clear('analytics');
  });
}

// Clean shutdown tracking (for crash/unexpected reboot detection)
const CLEAN_SHUTDOWN_KEY = 'govista_clean_shutdown';

export async function setCleanShutdown(value: boolean): Promise<void> {
  // Use localStorage for synchronous reliability (IndexedDB may not complete on shutdown)
  localStorage.setItem(CLEAN_SHUTDOWN_KEY, value ? 'true' : 'false');
}

export async function getCleanShutdown(): Promise<boolean> {
  const value = localStorage.getItem(CLEAN_SHUTDOWN_KEY);
  // First run (no key) is treated as clean
  if (value === null) return true;
  return value === 'true';
}
